import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppConfig } from '@app/core/app.config';
import { JMapParameters } from '@app/core/model/entities/external-api/jmap-parameters';
import { TranslateService } from '@ngx-translate/core';
import { GisService } from '@services/gis.service';
import { AppManager } from '@services/managers/app.manager';
import { PopupManager } from '@services/managers/popup.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';

@Component({
    selector: 'jmap',
    templateUrl: './jmap.component.html',
    styleUrls: ['./jmap.component.scss'],
    standalone: false
})
export class JmapComponent implements OnInit {

  @ViewChild('jmap') public jmap: ElementRef<HTMLIFrameElement>;

  protected jmapParameters: JMapParameters;
  protected jmapPanelContent: SafeResourceUrl;

  protected readonly appConfig: AppConfig = inject(AppConfig);
  private readonly gisService: GisService = inject(GisService);
  private readonly appManager: AppManager = inject(AppManager);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly snackbarManager: SnackbarManager = inject(SnackbarManager);
  private readonly popupManager: PopupManager = inject(PopupManager);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Init and load jMap parameters
   */
  public ngOnInit(): void {
    this.gisService.getJMapParameters(this.appManager.currentOrganization.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.jmapParameters = data;
        },
        complete: () => {
          if (this.jmapParameters.areCredentialsSet) {
            this.updateMapUrl();
          }
        }
      });
  }

  /**
   * Using the jMap service API, synchronises the data with the cartographic layer
   */
  public refreshData(): void {
    this.gisService.refreshJmapData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        complete: () => {
          this.updateMapUrl();
          this.snackbarManager.showActionSnackbar(this.translate.instant('MESSAGE.REFRESH_JMAP'));
        },
        error: () => {
          this.popupManager.showOkPopup({
            dialogTitle: this.translate.instant('TITLE.ERROR_REFRESH_JMAP'),
            dialogMessage: this.translate.instant('MESSAGE.ERROR_REFRESH_JMAP'),
            type: 'error'
          });
        }
      });
  }

  /**
   * Updates the jMap embed URL
   */
  private updateMapUrl(): void {
    if (this.jmapParameters.areCredentialsSet) {
      const mapUrl = `${this.jmapParameters.serverUrl}/?ngProjectId=${this.jmapParameters.projectId}&ngToken=${this.jmapParameters.sessionId}`;
      this.jmapPanelContent = this.sanitizer.bypassSecurityTrustResourceUrl(mapUrl);
    }
  }
}
